import { useEffect } from "react";

import PropTypes from "prop-types";
import { Controller, useFormContext } from "react-hook-form";

import { Paper } from "@mui/material";
import Grid from "@mui/material/Grid";

import { CustomerSelect } from "@/features/customers";
import { AllOutletsSelect, OutletRoleEnum, UserOutletsSelect } from "@/features/outlets";
import { CustomDatePicker, CustomInputField } from "@/features/ui";
import { OutletWarehouseSelect } from "@/features/warehouses";
import dayjs from "dayjs";

export const GeneralInformationSection = ({ editMode = false }) => {
  const {
    register,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const seller = watch("seller");
  const buyerableType = watch("buyerableType");
  const deliveryOrderProductVariants = watch("deliveryOrderProductVariants");

  // Disable buyer and seller when there are existing products
  const disableBuyerSeller = deliveryOrderProductVariants.length > 0;

  useEffect(() => {
    if (editMode) return;

    if (seller) {
      setValue("warehouse", null);
    }
  }, [editMode, seller, setValue]);

  // Auto-set buyerableType based on seller's role
  useEffect(() => {
    if (seller) {
      const buyerableType =
        seller.role === OutletRoleEnum.HUB ? "OUTLET" : "CUSTOMER";
      setValue("buyerableType", buyerableType);
    }
  }, [seller, setValue]);

  useEffect(() => {
    if (editMode) return;

    if (buyerableType) {
      setValue("buyer", null);
    }
  }, [buyerableType, editMode, setValue, watch]);

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="buyer"
            render={({ field, fieldState }) =>
              buyerableType === "OUTLET" ? (
                <AllOutletsSelect
                  value={field.value}
                  onChange={field.onChange}
                  disabled={editMode || disableBuyerSeller}
                  allowedRoles={[OutletRoleEnum.RETAIL]}
                  TextFieldProps={{
                    label: "Pelanggan (Outlet Retail)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              ) : (
                <CustomerSelect
                  value={field.value}
                  onChange={field.onChange}
                  disabled={editMode || disableBuyerSeller}
                  TextFieldProps={{
                    label: "Pelanggan (Retail)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              )
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="seller"
            render={({ field, fieldState }) => (
              <UserOutletsSelect
                value={field.value}
                onChange={field.onChange}
                disabled={editMode || disableBuyerSeller}
                allowedRoles={[OutletRoleEnum.HUB]}
                TextFieldProps={{
                  label: "Pemasok",
                  required: true,
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="warehouse"
            render={({ field, fieldState }) => (
              <OutletWarehouseSelect
                value={field.value}
                onChange={field.onChange}
                disabled={editMode || !seller || disableBuyerSeller}
                outletId={seller?.id}
                TextFieldProps={{
                  label: "Gudang Pemasok",
                  required: true,
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                  InputProps: {
                    placeholder: "Pilih Gudang",
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            name="transactionDate"
            control={control}
            render={({ field, fieldState }) => (
              <CustomDatePicker
                value={field.value}
                onChange={field.onChange}
                required
                label="Tanggal Pengiriman"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                maxDate={dayjs().add(2, "day")}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, lg: 8 }}>
          <CustomInputField
            name="note"
            label="Catatan"
            register={register}
            helperText={errors.note?.message}
            error={Boolean(errors.note)}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

GeneralInformationSection.propTypes = {
  editMode: PropTypes.bool,
};
